import { Icon } from "@/components/Library";
import { faArrowLeft, faArrowRight } from "@/icons/regular";
import { cn } from "@/utils/cnHelper";
import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";

interface CarouselProps {
  items: JSX.Element[];
  initialScroll?: number;
}

type Card = {
  id: number;
  title: string;
  imageUrl: string;
  linkUrl?: string;
};

export const Carousel = ({ items, initialScroll = 0 }: CarouselProps) => {
  const carouselRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(true);

  useEffect(() => {
    if (carouselRef.current) {
      carouselRef.current.scrollLeft = initialScroll;
      checkScrollability();
    }
  }, [initialScroll]);

  const checkScrollability = () => {
    if (carouselRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollLeft = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  return (
    <div className="tw-relative tw-w-full tw-space-y-4">
      <div className="tw-mr-10 tw-flex tw-justify-end tw-gap-2">
        <button
          className="tw-relative tw-z-40 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey_scale-800 disabled:tw-opacity-50"
          onClick={scrollLeft}
          disabled={!canScrollLeft}
        >
          <Icon icon={faArrowLeft} className="tw-h-4 tw-w-4 tw-text-white" />
        </button>
        <button
          className="tw-relative tw-z-40 tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey_scale-800 disabled:tw-opacity-50"
          onClick={scrollRight}
          disabled={!canScrollRight}
        >
          <Icon icon={faArrowRight} className="tw-h-4 tw-w-4 tw-text-white" />
        </button>
      </div>
      <div
        className="tw-flex tw-w-full tw-overflow-x-scroll tw-overscroll-x-auto tw-scroll-smooth tw-py-2 [scrollbar-width:none]"
        ref={carouselRef}
        onScroll={checkScrollability}
      >
        <div className={cn("tw-bg-gradient-to-l tw-absolute tw-right-0 tw-z-[1000] tw-h-auto tw-w-[5%] tw-overflow-hidden")}></div>

        <div className={cn("tw-flex tw-flex-row tw-justify-start tw-pl-4", "tw-mx-auto tw-max-w-7xl")}>
          {items.map((item, index) => (
            <motion.div
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.5,
                  delay: 0.2 * index,
                  ease: "easeOut",
                  once: true,
                },
              }}
              key={"card" + index}
              className="tw-rounded-3xl tw-p-2 last:tw-pr-[5%] md:last:tw-pr-[33%]"
            >
              {item}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const CarouselItemWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="tw-group tw-relative tw-z-10 tw-flex tw-h-full tw-w-56 tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-bg-grey_scale-800/50 tw-transition-all hover:tw-shadow-2xl md:tw-w-96">
      {children}
    </div>
  );
};

export const Card = ({ card }: { card: Card }) => {
  const cardContent = (
    <div className="tw-group tw-relative tw-z-10 tw-flex tw-h-80 tw-w-56 tw-flex-col tw-overflow-hidden tw-rounded-3xl tw-bg-grey_scale-800/50 tw-transition-all hover:tw-shadow-2xl md:tw-h-[40rem] md:tw-w-96">
      <div className="tw-relative tw-h-full tw-w-full tw-overflow-hidden">
        <BlurImage
          src={card.imageUrl}
          alt={card.title}
          fill
          className="tw-absolute tw-inset-0 tw-z-10 tw-object-cover tw-transition-transform tw-duration-300 group-hover:tw-scale-110"
        />
      </div>
      <div className="tw-w-full tw-p-2">
        <div className="tw-text-balance tw-text-center tw-text-lg tw-font-semibold tw-text-white md:tw-text-xl">{card.title}</div>
      </div>
    </div>
  );

  if (card.linkUrl) {
    return (
      <a
        href={card.linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="tw-block tw-rounded-3xl tw-no-underline hover:tw-no-underline focus:tw-outline-none focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-4 focus-visible:tw-outline-brand_scale-500"
      >
        {cardContent}
      </a>
    );
  }

  return cardContent;
};

export const BlurImage = ({ height, width, src, className, alt, ...rest }: any) => {
  const [isLoading, setLoading] = useState(true);
  return (
    <img
      className={cn("tw-h-full tw-w-full tw-transition tw-duration-300", isLoading ? "tw-blur-sm" : "tw-blur-0", className)}
      onLoad={() => setLoading(false)}
      src={src as string}
      width={width}
      height={height}
      loading="lazy"
      decoding="async"
      blurDataURL={typeof src === "string" ? src : undefined}
      alt={alt ? alt : "Background of a beautiful view"}
      {...rest}
    />
  );
};
